import { api } from "../../../redux/api";
import { rcTreeToWorkflowTemplate } from "../../../utils/rctreeDataConversion";
import { toMilliseconds } from "./validateApiServiceWizard";
import { apiT, K } from "./apiServiceI18n";

/** Workbench request source — suppresses generic workflow-template audit when deploying API services. */
export const API_SERVICE_MANAGER_SOURCE = "api_service_manager";

export function isWorkbenchSuccess(res) {
  return res?.data?.current_state === "SUCCESS";
}

export function getWorkbenchMessage(res, fallback = "") {
  return res?.data?.message || fallback;
}

/** Build custom_url_query JSON from Custom URL drawer param rows. */
export function buildCustomUrlQuery(params = []) {
  const query = {};
  params.forEach(({ name, value }) => {
    const k = (name || "").trim();
    if (!k) return;
    query[k] = (value ?? "").trim();
  });
  return Object.keys(query).length ? query : null;
}

export function customUrlQueryString(queryObj) {
  if (!queryObj || !Object.keys(queryObj).length) return "";
  return JSON.stringify(queryObj);
}

/** Custom URL path tail sent as `custom_url` (drawer “From” field per BR4.1). */
export function resolveCustomUrlPath(customUrl) {
  return (customUrl?.from || "").trim();
}

/** Append additional-parameter rows as a query string to a base/target URL. */
export function appendQueryParamsToUrl(baseUrl, params = []) {
  const base = (baseUrl || "").trim();
  if (!base) return "";
  const queryObj = buildCustomUrlQuery(params);
  if (!queryObj) return base;
  const qs = new URLSearchParams(queryObj).toString();
  if (!qs) return base;
  return `${base}${base.includes("?") ? "&" : "?"}${qs}`;
}

/** Public custom URL preview: https://[SERVER_URL]/execute/<from>. */
export function formatCustomUrlPublicPreview(from) {
  const path = (from || "").trim().replace(/^\/+/, "").replace(/^execute\/+/i, "");
  if (!path) return "";
  return `https://[SERVER_URL]/execute/${path}`;
}

/** Map api_service_get_detail `custom_url_query` to drawer param rows. */
export function parseCustomUrlQuery(query) {
  if (query == null || query === "") return [];
  let obj = query;
  if (typeof query === "string") {
    try {
      obj = JSON.parse(query);
    } catch {
      return [];
    }
  }
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return [];
  return Object.entries(obj).map(([name, value]) => ({
    name,
    value: value == null ? "" : String(value),
  }));
}

/** Load drawer state from detail API fields. */
export function customUrlFromDetail({ custom_url, custom_url_query } = {}) {
  return {
    from: (custom_url || "").trim(),
    params: parseCustomUrlQuery(custom_url_query),
  };
}

function validationUnavailable(res) {
  const msg = (getWorkbenchMessage(res) || "").toLowerCase();
  return (
    res?.error ||
    res?.data?.current_state === "ERROR" ||
    msg.includes("no handler") ||
    msg.includes("unknown action") ||
    msg.includes("not found")
  );
}

function mapDuplicateServiceReason(data) {
  if (!data || data.available !== false) return null;
  if (data.reason === "DUPLICATE_SERVICE_NAME" && data.wf_group && data.wf_name) {
    return apiT(K.API_SVC_DUPLICATE_SERVICE, { 0: data.wf_group, 1: data.wf_name });
  }
  return data.reason ? String(data.reason).replace(/_/g, " ") : apiT(K.API_SVC_SERVICE_NOT_AVAILABLE);
}

/**
 * api_service_validate_service_name (BR4.1) with graceful fallback if action missing.
 */
export async function validateApiServiceName(dispatch, { account, serviceName }) {
  const name = (serviceName || "").trim();
  if (!name) return { ok: false, message: apiT(K.API_SVC_REQUIRED) };

  const res = await dispatch(
    api.endpoints.apiServiceValidateServiceName.initiate(
      { account, service_name: name },
      { forceRefetch: true }
    )
  );

  if (validationUnavailable(res)) {
    return { ok: true, skipped: true };
  }

  if (!isWorkbenchSuccess(res)) {
    return { ok: false, message: getWorkbenchMessage(res, apiT(K.API_SVC_VALIDATE_SERVICE_FAILED)) };
  }

  const data = res.data?.data;
  if (data?.available === false) {
    return { ok: false, message: mapDuplicateServiceReason(data) || apiT(K.API_SVC_SERVICE_NOT_AVAILABLE) };
  }

  return { ok: true };
}

/**
 * api_service_validate_workflow_name (BR4.1), fallback validate_workflow_template.
 */
export async function validateApiWorkflowName(
  dispatch,
  { account, workflowGroup, workflowName }
) {
  const wfg = (workflowGroup || "").trim();
  const wf = (workflowName || "").trim();
  if (!wfg) return { ok: false, message: apiT(K.API_SVC_SELECT_WF_GROUP) };
  if (!wf) return { ok: false, message: apiT(K.API_SVC_REQUIRED) };

  const res = await dispatch(
    api.endpoints.apiServiceValidateWorkflowName.initiate(
      { account, wfg_name: wfg, wf_name: wf },
      { forceRefetch: true }
    )
  );

  if (!validationUnavailable(res) && isWorkbenchSuccess(res)) {
    const data = res.data?.data;
    if (data?.available === false) {
      const reason =
        data.reason === "WORKFLOW_EXISTS"
          ? apiT(K.API_SVC_WF_EXISTS)
          : data.reason
            ? String(data.reason).replace(/_/g, " ")
            : apiT(K.API_SVC_WF_NOT_AVAILABLE);
      return { ok: false, message: reason };
    }
    return { ok: true };
  }

  const legacy = await dispatch(
    api.endpoints.validateWorkflowTemplate.initiate(
      {
        account,
        workflowGroupName: wfg,
        templateName: wf,
        data: {},
      },
      { forceRefetch: true }
    )
  );

  if (!isWorkbenchSuccess(legacy)) {
    return {
      ok: false,
      message: getWorkbenchMessage(legacy, apiT(K.API_SVC_WF_VALIDATE_FAILED)),
    };
  }

  return { ok: true, usedLegacyValidator: true };
}

/**
 * api_service_validate_custom_url (BR4.1) for BridgeGateHTTP.
 */
export async function validateApiCustomUrl(
  dispatch,
  { account, customUrl, workflowGroup, workflowName }
) {
  const path = resolveCustomUrlPath(customUrl);
  const queryObj = buildCustomUrlQuery(customUrl?.params);
  const queryStr = customUrlQueryString(queryObj);

  const res = await dispatch(
    api.endpoints.apiServiceValidateCustomUrl.initiate(
      {
        account,
        custom_url: path,
        custom_url_query: queryStr,
        wfg_name: workflowGroup,
        wf_name: workflowName,
      },
      { forceRefetch: true }
    )
  );

  if (validationUnavailable(res)) {
    return { ok: true, skipped: true };
  }

  if (!isWorkbenchSuccess(res)) {
    return { ok: false, message: getWorkbenchMessage(res, apiT(K.API_SVC_VALIDATE_URL_FAILED)) };
  }

  const data = res.data?.data;
  if (data?.reason === "QUERY_WITHOUT_PATH") {
    return { ok: false, message: apiT(K.API_SVC_CUSTOM_URL_QUERY_WITHOUT_PATH) };
  }
  if (data?.valid === false || data?.conflict) {
    if (data.owner_wfg_name && data.owner_wf_name) {
      return {
        ok: false,
        message: apiT(K.API_SVC_CUSTOM_URL_IN_USE, {
          0: data.owner_wfg_name,
          1: data.owner_wf_name,
        }),
      };
    }
    return { ok: false, message: apiT(K.API_SVC_CUSTOM_URL_INVALID) };
  }

  return { ok: true, normalizedKey: data?.normalized_execute_key };
}

/** Server/userspace workflow files are keyed by lowercase name (see WorkflowAPI.deployToServer). */
export function normalizeWorkflowKey(name) {
  return (name || "").trim().toLowerCase();
}

/** Ensure workflow JSON WORKFLOW.NAME matches server/userspace file key (lowercase). */
export function applyApiServiceFormToWorkflowPayload(payload, form) {
  if (!payload?.WORKFLOW_DEFINITION) return payload;

  const wfKey = normalizeWorkflowKey(form.workflowName);
  if (!wfKey) return payload;

  payload.WORKFLOW_DEFINITION.WORKFLOW = {
    ...(payload.WORKFLOW_DEFINITION.WORKFLOW || {}),
    NAME: wfKey,
  };

  const apiName = (form.apiName || "").trim();
  const description = (form.description || "").trim();
  const serviceFields = {
    ...(payload.WORKFLOW_DEFINITION.SERVICE || {}),
    ...(apiName ? { API_SERVICE_NAME: apiName } : {}),
    ENABLED: payload.WORKFLOW_DEFINITION.SERVICE?.ENABLED || "TRUE",
    ...(description ? { DESCRIPTION: description } : {}),
  };

  if (form.conformance && (form.executeWithin || "").trim()) {
    const ms = toMilliseconds(form.executeWithin, form.executeUnit);
    if (ms != null) {
      serviceFields.CONFORMANCE_EXECUTE_WITHIN_MS = String(ms);
    }
    // "Has no errors" is enforced at runtime via isPureSuccess(); do not map to errors_only (metrics-only flag).
    serviceFields.CONFORMANCE_ERRORS_ONLY = "FALSE";
  }

  payload.WORKFLOW_DEFINITION.SERVICE = serviceFields;

  return payload;
}

/** create_workflow_template — create empty workflow in user space (Step 1 Next). */
export async function createWorkflowTemplateStep1(dispatch, { account, workflowGroup, workflowName }) {
  const wfKey = normalizeWorkflowKey(workflowName);
  return dispatch(
    api.endpoints.addWorkflowTemplate.initiate(
      {
        account,
        workflow_group_name: encodeURIComponent(workflowGroup),
        workflow_name: encodeURIComponent(wfKey),
        type: "NEW",
        source_workflow_name: "",
        source_workflow_group_name: "",
        source_account: account,
      },
      { forceRefetch: true }
    )
  );
}

/** Run all BR4.1 Step 1 server validations before create. */
export async function runStep1ServerValidation(dispatch, form, { account }) {
  const nameResult = await validateApiServiceName(dispatch, {
    account,
    serviceName: form.apiName,
  });
  if (!nameResult.ok) return { ok: false, field: "apiName", message: nameResult.message };

  const wfResult = await validateApiWorkflowName(dispatch, {
    account,
    workflowGroup: form.workflowGroup,
    workflowName: form.workflowName,
  });
  if (!wfResult.ok) return { ok: false, field: "workflowName", message: wfResult.message };

  if (form.apiType === "BridgeGateHTTP") {
    const urlResult = await validateApiCustomUrl(dispatch, {
      account,
      customUrl: form.customUrl,
      workflowGroup: form.workflowGroup,
      workflowName: form.workflowName,
    });
    if (!urlResult.ok) return { ok: false, field: "customUrl", message: urlResult.message };
  }

  return { ok: true };
}

/** Build workflow tree shape expected by {@link rcTreeToWorkflowTemplate}. */
export function workflowSnapshotToTree(snapshot) {
  return {
    templateHeader: snapshot?.templateHeader || {},
    body: {
      translatedData: snapshot?.translatedData || [],
      selectedNode: snapshot?.selectedNode || {},
    },
    header: { translatedData: [], selectedNode: {} },
    trailer: { translatedData: [], selectedNode: {} },
  };
}

/**
 * BR4.4 Finish: save workflow (unsaved session, userspace, deploy to server), apply API metadata, enable service.
 * Uses deploy_workflow_template_to_server so the server .json exists before api_service_update (reads accountRootDir).
 * @see API_SERVICE_MANAGEMENT.md — save_workflow_template, api_service_update, change_status
 */
export async function deployApiServiceWizard(dispatch, { account, form, workflowSnapshot }) {
  const wfg = (form.workflowGroup || "").trim();
  const wf = normalizeWorkflowKey(form.workflowName);
  if (!account || !wfg || !wf) {
    return { ok: false, message: apiT(K.API_SVC_SAVE_PREREQ) };
  }

  const tree = workflowSnapshotToTree(workflowSnapshot);
  let workflowPayload = rcTreeToWorkflowTemplate(tree);
  workflowPayload = applyApiServiceFormToWorkflowPayload(workflowPayload, { ...form, workflowName: wf });

  // Step 1: store in session unsaved list (same as main workbench SaveButton before deploy).
  const unsavedRes = await dispatch(
    api.endpoints.saveUserWorkflowTemplate.initiate(
      {
        account,
        workflowGroupName: wfg,
        templateName: encodeURIComponent(wf),
        data: workflowPayload,
      },
      { forceRefetch: true }
    )
  );

  if (!isWorkbenchSuccess(unsavedRes)) {
    return {
      ok: false,
      message: getWorkbenchMessage(unsavedRes, apiT(K.API_SVC_SAVE_WF_FAILED)),
      step: "save_workflow_template_unsaved",
    };
  }

  // Step 2: write full workflow JSON to userspace (create_workflow_template only created an empty shell).
  const userspaceRes = await dispatch(
    api.endpoints.saveWorkflowTemplate.initiate(
      {
        account,
        workflowGroupName: wfg,
        workflowName: wf,
        data: workflowPayload,
        save_to_server: "FALSE",
      },
      { forceRefetch: true }
    )
  );

  if (!isWorkbenchSuccess(userspaceRes)) {
    return {
      ok: false,
      message: getWorkbenchMessage(userspaceRes, apiT(K.API_SVC_SAVE_WF_FAILED)),
      step: "save_workflow_template_userspace",
    };
  }

  // Step 3: deploy userspace -> server (same path as workbench Deploy to Server; lowercase filename).
  const deployRes = await dispatch(
    api.endpoints.deployWorkflowTemplateToServer.initiate(
      {
        account,
        workflowGroupName: wfg,
        templateName: wf,
        comment: "API Service Manager — create service",
        source: API_SERVICE_MANAGER_SOURCE,
      },
      { forceRefetch: true }
    )
  );

  if (!isWorkbenchSuccess(deployRes)) {
    return {
      ok: false,
      message: getWorkbenchMessage(deployRes, apiT(K.API_SVC_SAVE_WF_SERVER_FAILED)),
      step: "deploy_workflow_template_to_server",
    };
  }

  // Enable before metadata update: change_status saveWorkflow writes in-memory JSON to disk and
  // would clobber api_service_update fields if run afterward.
  const statusRes = await dispatch(
    api.endpoints.apiServiceChangeStatus.initiate(
      {
        account,
        wfg_name: wfg,
        wf_name: wf,
        service_status: true,
        source: API_SERVICE_MANAGER_SOURCE,
      },
      { forceRefetch: true }
    )
  );

  if (!isWorkbenchSuccess(statusRes)) {
    return {
      ok: false,
      message: getWorkbenchMessage(statusRes, apiT(K.API_SVC_ENABLE_SERVICE_FAILED)),
      step: "change_status",
    };
  }

  const updateParams = {
    account,
    wfg_name: wfg,
    wf_name: wf,
    service_name: (form.apiName || "").trim(),
    service_description: (form.description || "").trim(),
    source: API_SERVICE_MANAGER_SOURCE,
  };
  if (form.conformance && (form.executeWithin || "").trim()) {
    const ms = toMilliseconds(form.executeWithin, form.executeUnit);
    if (ms != null) updateParams.conformance_execute_within_ms = ms;
    updateParams.conformance_errors_only = false;
  }

  if (form.apiType === "BridgeGateHTTP") {
    updateParams.custom_url = resolveCustomUrlPath(form.customUrl);
    const queryObj = buildCustomUrlQuery(form.customUrl?.params);
    const queryStr = customUrlQueryString(queryObj);
    if (queryStr) updateParams.custom_url_query = queryStr;
  }

  const updateRes = await dispatch(
    api.endpoints.apiServiceUpdate.initiate(updateParams, { forceRefetch: true })
  );

  if (!isWorkbenchSuccess(updateRes)) {
    return {
      ok: false,
      message: getWorkbenchMessage(updateRes, apiT(K.API_SVC_UPDATE_METADATA_FAILED)),
      step: "api_service_update",
    };
  }

  return { ok: true };
}

/**
 * Apply all edits from the Edit API screen: metadata update + optional status change.
 * `detail` is the original object from mapApiServiceDetailResponse.
 * `form` is the current edit-form state (from mapApiServiceDetailToForm + user edits).
 */
export async function applyApiServiceEdit(dispatch, { detail, form }) {
  const updateParams = {
    account: detail.account,
    wfg_name: detail.workflowGroup,
    wf_name: detail.workflowName,
    service_name: (form.apiName || "").trim(),
    service_description: (form.description || "").trim(),
    source: API_SERVICE_MANAGER_SOURCE,
  };

  // Always send conformance fields so clearing is persisted.
  if (form.conformance && form.conformanceExecute && (form.executeWithin || "").trim()) {
    const ms = toMilliseconds(form.executeWithin, form.executeUnit);
    updateParams.conformance_execute_within_ms = ms != null ? ms : 0;
  } else {
    updateParams.conformance_execute_within_ms = 0;
  }
  updateParams.conformance_errors_only = false;

  if (detail.adapter === "BridgeGateHTTP") {
    updateParams.custom_url = resolveCustomUrlPath(form.customUrl);
    const queryObj = buildCustomUrlQuery(form.customUrl?.params);
    const queryStr = customUrlQueryString(queryObj);
    if (queryStr) updateParams.custom_url_query = queryStr;
  }

  const updateRes = await dispatch(
    api.endpoints.apiServiceUpdate.initiate(updateParams, { forceRefetch: true })
  );

  if (!isWorkbenchSuccess(updateRes)) {
    return {
      ok: false,
      message: getWorkbenchMessage(updateRes, apiT(K.API_SVC_UPDATE_METADATA_FAILED)),
      step: "api_service_update",
    };
  }

  // Change status only if it differs from the original.
  if (form.enabled !== detail.enabled) {
    const statusRes = await dispatch(
      api.endpoints.apiServiceChangeStatus.initiate(
        {
          account: detail.account,
          wfg_name: detail.workflowGroup,
          wf_name: detail.workflowName,
          service_status: form.enabled,
          source: API_SERVICE_MANAGER_SOURCE,
        },
        { forceRefetch: true }
      )
    );

    if (!isWorkbenchSuccess(statusRes)) {
      return {
        ok: false,
        message: getWorkbenchMessage(statusRes, apiT(K.API_SVC_ENABLE_SERVICE_FAILED)),
        step: "change_status",
      };
    }
  }

  return { ok: true };
}

/** Parse api_service_management from get_user_permission when present. */
export function parseApiServicePermissions(responseData) {
  const bag = responseData?.api_service_management;
  if (bag && typeof bag === "object") {
    return {
      view: Boolean(bag.view),
      modify: Boolean(bag.modify),
    };
  }

  const list = Array.isArray(responseData?.data)
    ? responseData.data
    : Array.isArray(responseData)
      ? responseData
      : [];
  const upper = list.map((p) => String(p).toUpperCase());
  const modify =
    upper.includes("ADMIN_RIGHTS") ||
    upper.includes("SERVICES_RIGHTS") ||
    upper.includes("API_MANAGEMENT_RIGHTS");
  const view = modify || upper.includes("SERVICES_RIGHTS_VIEW_ONLY") || upper.includes("API_MANAGEMENT_RIGHTS_VIEW_ONLY");

  return { view, modify };
}
